import {
  Directive,
  ElementRef,
  inject,
  input,
  ViewContainerRef,
  signal,
  computed,
  afterNextRender,
  Injector
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { merge, of, Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { OverlayOriginDirective } from '../overlay/overlay-origin.directive';
import { KeyCode } from '../../domain/key-code.enum';
import { PlatformService } from '../../services/platform.service';
import { FlyoutMenuComponent } from './flyout-menu.component';
import { FlyoutMenuItemDirective } from './flyout-menu-item.directive';

import type { PositionX, PositionY } from '../overlay/overlay-origin.directive';
import type { Observable } from 'rxjs';
import type {
  CloseScrollStrategy,
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  OverlayRef,
  PositionStrategy,
  RepositionScrollStrategy
} from '@angular/cdk/overlay';
import type { InputSignal, OnDestroy, OnInit, Signal, WritableSignal } from '@angular/core';

import type { FlyoutMenuGlobalPosition, FlyoutMenuCloseReason } from './flyout-menu.component';

/**
 * This directive is intended to be used in conjunction with an td-wb-flyout-menu tag.  It is
 * responsible for toggling the display of the provided menu.
 *
 * Keyboard interaction
 *  DOWN_ARROW: Focuses the next menu item
 *  UP_ARROW: Focuses previous menu item
 *  RIGHT_ARROW: Opens the menu item's sub-menu
 *  ENTER: Activates the focused menu item
 */
@Directive({
  selector: '[tdWbFlyoutMenuToggle]',
  exportAs: 'tdWbFlyoutMenuToggle',
  host: {
    '[attr.aria-haspopup]': 'haspopup',
    '[attr.aria-expanded]': 'attrAriaExpanded()',
    '[attr.aria-controls]': 'attrAriaControls()',
    '(click)': 'onClick($event)',
    '(keydown)': 'onKeyDown($event, $event.key)'
  }
})
export class FlyoutMenuToggleDirective extends OverlayOriginDirective implements OnInit, OnDestroy {
  private static readonly ROOT_POSITION: Array<string> = ['start-bottom', 'start-top'];
  private static readonly CHILD_POSITION: Array<string> = ['center-right', 'center-left'];

  readonly menu: InputSignal<FlyoutMenuComponent | undefined> = input<FlyoutMenuComponent>(undefined, { alias: 'tdWbFlyoutMenuToggle' });
  override readonly overlayPosition: InputSignal<string | Array<string> | undefined> = input<string | Array<string> | undefined>(undefined, { alias: 'tdWbFlyoutMenuToggleOverlayPosition' });
  readonly scrollStrategy: InputSignal<'close' | 'reposition'> = input<'close' | 'reposition'>('close', { alias: 'tdWbFlyoutMenuToggleScrollStrategy' });
  readonly positionStrategy: InputSignal<'global' | 'flexibleConnected'> = input<'global' | 'flexibleConnected'>('flexibleConnected', { alias: 'tdWbFlyoutMenuTogglePositionStrategy' });
  readonly globalPosition: InputSignal<FlyoutMenuGlobalPosition | undefined> = input<FlyoutMenuGlobalPosition>(undefined, { alias: 'tdWbFlyoutMenuToggleGlobalPosition' });
  /**
   * When true the flyout will allow page scrolling while opened. This disables the overlay
   * backdrop and uses a document click listener to detect outside clicks and close the menu.
   */
  readonly allowPageScroll: InputSignal<boolean> = input<boolean>(false, { alias: 'tdWbFlyoutMenuToggleAllowPageScroll' });

  haspopup: boolean = true;

  readonly menuOpened: WritableSignal<boolean> = signal<boolean>(false);

  readonly attrAriaExpanded: Signal<string | null> = computed(() => this.menuOpened() ? 'true' : null);

  readonly attrAriaControls: Signal<string | undefined> = computed(() => {
    const menu = this.menu();
    return (this.menuOpened() && menu) ? menu.getMenuId() : undefined;
  });

  protected override readonly DEFAULT_OFFSET: number = 0;
  private menuSelectSubscription: Subscription = Subscription.EMPTY;
  private closingActionsSubscription: Subscription = Subscription.EMPTY;
  private overlayRef: OverlayRef | undefined;
  private platformIOS: boolean = false;

  override readonly elementRef: ElementRef = inject(ElementRef);
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly overlay: Overlay = inject(Overlay);
  private readonly injector: Injector = inject(Injector);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly parentMenu: FlyoutMenuComponent | null = inject(FlyoutMenuComponent, { optional: true, host: true });
  private readonly menuItem: FlyoutMenuItemDirective | null = inject(FlyoutMenuItemDirective, { optional: true, self: true });

  constructor() {
    super();
  }

  protected override getPositions(): Array<ConnectedPosition> {
    if (this.overlayPosition() !== undefined) {
      return super.getPositions();
    }
    const defaultPosition = this.triggersSubmenu() ? FlyoutMenuToggleDirective.CHILD_POSITION : FlyoutMenuToggleDirective.ROOT_POSITION;
    const connectedPositions: Array<ConnectedPosition> = [];
    defaultPosition.forEach((pos: string): void => {
      connectedPositions.push(this.getConnectedPosition(this.parsePositions(pos)));
    });
    return connectedPositions;
  }

  /*
  * Item click handler
  * Open flyout menu
  */
  onClick(event: Event): void {
    if (this.triggersSubmenu()) {
      event.stopPropagation();
    }
    this.toggleMenu();
  }

  onKeyDown(event: KeyboardEvent, keyCode: string): void {
    if (keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE || keyCode === KeyCode.ARROW_RIGHT) {
      this.toggleMenu();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  ngOnInit(): void {
    const menu = this.menu();
    if (menu !== undefined) {
      this.menuSelectSubscription = outputToObservable(menu.close)
        .pipe(
          filter((reason: FlyoutMenuCloseReason): boolean =>
            reason === 'select' || reason === 'keyboard' || reason === 'accessibility'))
        .subscribe((reason: void | 'keyboard' | 'select' | 'accessibility'): void => {
          if (this.parentMenu !== null) {
            this.parentMenu.close.emit(reason);
          } else {
            this.closeMenu();
          }
        });
    }
    this.platformIOS = this.platformService.isIOS();

    if (this.menuItem !== null) {
      this.menuItem.triggersSubmenu.set(this.triggersSubmenu());
    }
  }

  ngOnDestroy(): void {
    this.closeMenu();

    this.menuSelectSubscription.unsubscribe();
    this.closingActionsSubscription.unsubscribe();
  }

  /** Toggles the menu between the open and closed states. */
  private toggleMenu(): void {
    this.menuOpened() ? this.closeMenu() : this.openMenu();
  }

  /** Whether the menu triggers a sub-menu or a top-level one. */
  private triggersSubmenu(): boolean {
    return (this.menuItem !== null) && (this.parentMenu !== null);
  }

  /*
  * Open menu / create overlay
  */
  private openMenu(): void {
    const menu = this.menu();
    if (!this.menuOpened() && menu) {
      this.createOverlay();
      this.setIsMenuOpen(true);
      menu.parentMenu = this.triggersSubmenu() ? this.parentMenu : undefined;
      menu.focusFirstItemOrSelected();
      this.closingActionsSubscription.unsubscribe();
      this.closingActionsSubscription = this.menuClosingActions().subscribe((): void => {
        this.closeMenu();
      });
    }
  }

  /*
  * Close overlay
  */
  private closeMenu(): void {
    if (this.menuOpened() === true) {
      this.setIsMenuOpen(false);
      if (this.overlayRef !== undefined) {
        this.overlayRef.dispose();
        this.overlayRef = undefined;
      }
      if (this.platformIOS) {
        setTimeout((): void => this.focus(), 500);
      } else {
        this.focus();
      }
    }
  }

  private focus(): void {
    if (!this.triggersSubmenu()) {
      this.elementRef.nativeElement.focus();
    }
  }

  /*
  * Set menu status open/closed
  */
  private setIsMenuOpen(isOpen: boolean): void {
    this.menuOpened.set(isOpen);
    const menu = this.menu();
    if (menu) {
      isOpen ? menu.open.emit() : menu.close.emit();
      menu.setIsMenuOpen(isOpen);
    }

    if (this.triggersSubmenu()) {
      this.menuItem.highlighted.set(isOpen);
    }
  }

  /*
  * Observable for menu close items
  */
  private menuClosingActions(): Observable<unknown> {
    const backdrop = this.overlayRef !== undefined ? this.overlayRef.backdropClick() : of();
    const detachments = this.overlayRef !== undefined ? this.overlayRef.detachments() : of();
    const parentClose = (this.parentMenu !== null) ? outputToObservable(this.parentMenu.close).pipe(
      filter((reason: FlyoutMenuCloseReason): boolean => reason !== 'select')
    ) : of();
    const parentMenuClicked = (this.parentMenu !== null) ? this.parentMenu.itemClicked().pipe(
      filter((item: FlyoutMenuItemDirective): boolean => item !== this.menuItem),
      filter((): boolean => this.menuOpened())
    ) : of();

    // When page scrolling is allowed, we listen for document click events (not
    // pointerdown) to avoid closing the menu when the user starts a touch/drag
    // scroll or swipe. Click events are not fired for touch scrolling, so this
    // prevents accidental closes during swipes.
    let docClick: Observable<unknown> = of();
    if (this.allowPageScroll() && typeof this.elementRef?.nativeElement?.ownerDocument !== 'undefined') {
      docClick = fromEvent<MouseEvent>(this.elementRef.nativeElement.ownerDocument, 'click').pipe(
        filter((event: MouseEvent): boolean => {
          const target = event.target as Node | null;
          const overlayEl = this.overlayRef?.overlayElement;
          // If the click is inside the overlay or inside the trigger element, ignore
          if (!target) return false;
          return !(overlayEl?.contains(target) || this.elementRef?.nativeElement?.contains(target));
        })
      );
    }

    return merge(backdrop, detachments, parentClose, parentMenuClicked, docClick);
  }

  /**
   * Creates the overlay into which the dialog will be loaded.
   *
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(): void {
    const positions = this.calculatePreferredPositions();
    const overlayConfig = this.getOverlayConfig(this.viewContainerRef.element, positions);
    this.overlayRef = this.overlay.create(overlayConfig);

    const portal = new TemplatePortal(this.menu().templateRef, this.viewContainerRef);
    this.overlayRef.attach(portal);

    afterNextRender(() => {
      this.overlayRef?.updatePosition();
    }, { injector: this.injector });
  }

  private calculatePreferredPositions(): Array<ConnectedPosition> {
    if (this.triggersSubmenu() || this.overlayPosition() !== undefined) {
      return this.getPositions();
    }

    const toggleRect = this.elementRef.nativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - toggleRect.bottom;
    const spaceAbove = toggleRect.top;

    const allPositions = this.getPositions();
    if (allPositions.length < 2) {
      return allPositions;
    }

    const bottomPositions = allPositions.filter((p: ConnectedPosition) => p.originY === 'bottom');
    const topPositions = allPositions.filter((p: ConnectedPosition) => p.originY === 'top');

    if (bottomPositions.length === 0 || topPositions.length === 0) {
      return allPositions;
    }

    if (spaceBelow >= spaceAbove) {
      return bottomPositions;
    } else {
      return topPositions;
    }
  }

  /**
   * @returns The overlay configuration.
   */
  private getOverlayConfig(elementRef: ElementRef, positions: Array<ConnectedPosition>): OverlayConfig {
    let positionStrategy: PositionStrategy;

    if (this.positionStrategy() === 'global') {
      positionStrategy = this.overlay
        .position()
        .global()
        .centerHorizontally();

      const globalPos = this.globalPosition();
      if (globalPos?.top !== undefined) {
        (positionStrategy as GlobalPositionStrategy).top(globalPos.top);
      }
    } else {
      positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(elementRef)
        .withPositions(positions)
        .withPush(false)
        .withFlexibleDimensions(false)
        .withGrowAfterOpen(true)
        .withLockedPosition(false);

      (positionStrategy as FlexibleConnectedPositionStrategy).positionChanges.subscribe((change: ConnectedOverlayPositionChange): void => {
        this.setPosition(change);
      });

    }

    let scrollStrategy: RepositionScrollStrategy | CloseScrollStrategy;

    // If the menu allows page scrolling, avoid using the `close` scroll strategy
    // because it closes the overlay on scroll. Prefer `reposition` so the overlay
    // remains open and repositions itself when the page is scrolled.
    if (this.scrollStrategy() === 'reposition' || this.allowPageScroll()) {
      scrollStrategy = this.overlay.scrollStrategies.reposition();
    } else {
      scrollStrategy = this.overlay.scrollStrategies.close();
    }

    const hasBackdrop = !this.triggersSubmenu() && !this.allowPageScroll();

    return new OverlayConfig({
      panelClass: 'td-wb-overlay-panel',
      positionStrategy,
      hasBackdrop,
      backdropClass: hasBackdrop ? 'cdk-overlay-transparent-backdrop' : undefined,
      scrollStrategy
    });
  }

  /**
   * Set positions classes
   */
  private setPosition(change: ConnectedOverlayPositionChange): void {
    let position: PositionX | PositionY;
    if (this.parentMenu !== null && this.parentMenu !== undefined) {
      position = change.connectionPair.originX as PositionX;
    } else {
      position = change.connectionPair.originY as PositionY;
    }
    const menu = this.menu();
    if (menu) {
      menu.overlayPosition = position;
    }
  }
}
